import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { isLowPower } from "../utils/perf";
import { isMusicOn, onMusicChange } from "../utils/sound";
import { getTempo, onTempoChange } from "../utils/tempo";

// Tech + stars slowly circling the RIM of the site like a calm asteroid
// belt — a dual-axis (Lissajous) ellipse keeps every item hugging the
// screen edges so nothing ever parks in the middle of the content.
// Rim radii are sized so every ellipse stays OUTSIDE the central content
// rectangle at all phases: (0.275vw/rx)² + (0.30vh/ry)² ≥ 1 with margin
// for the ease-in-out bulge — rx ≥ 50vw, ry ≥ 45vh always.
const ORBITERS = [
  { img: "/devices/airpods.png", size: 42, rx: 52, ry: 47, dur: 58, phase: 0, opacity: 0.3 },
  { img: "/devices/watch-galaxy.png", size: 34, rx: 50, ry: 45.5, dur: 74, phase: 21, rev: true, opacity: 0.26, mobileHide: true },
  { img: "/devices/speaker.png", size: 46, rx: 53, ry: 48, dur: 66, phase: 33, opacity: 0.22, mobileHide: true },
  { img: "/devices/keyboard-rgb.png", size: 40, rx: 51, ry: 46, dur: 80, phase: 47, rev: true, opacity: 0.18, mobileHide: true },
  { star: true, size: 10, rx: 52, ry: 47, dur: 46, phase: 9, rev: true, opacity: 0.85 },
  { star: true, size: 7, rx: 50, ry: 45, dur: 40, phase: 27, opacity: 0.7 },
  { star: true, size: 8, rx: 53, ry: 48, dur: 64, phase: 41, opacity: 0.8, mobileHide: true },
  { star: true, size: 6, rx: 50.5, ry: 46, dur: 52, phase: 15, opacity: 0.65 },
];

const Orbiter = ({ o, reduced }) => (
  <div
    className={`absolute left-1/2 top-1/2 ${o.mobileHide ? "hidden sm:block" : ""}`}
    style={{
      // longhands only — the `animation` shorthand resets delay when the
      // duration string changes on tempo re-render (that stranded the phase)
      "--rx": `${o.rx}vw`,
      animationName: "orbit-x",
      animationDuration: `${o.dur}s`,
      animationTimingFunction: "ease-in-out",
      animationIterationCount: "infinite",
      animationDelay: `${-o.phase}s`,
      animationDirection: o.rev ? "reverse" : "normal",
      animationPlayState: reduced ? "paused" : "running",
    }}
    aria-hidden="true"
  >
    <div
      style={{
        "--ry": `${o.ry}vh`,
        animationName: "orbit-y",
        animationDuration: `${o.dur}s`,
        animationTimingFunction: "ease-in-out",
        animationIterationCount: "infinite",
        animationDelay: `${-o.phase - o.dur / 4}s`,
        animationPlayState: reduced ? "paused" : "running",
      }}
    >
      {o.star ? (
        <span
          className="block rounded-full"
          style={{
            width: o.size,
            height: o.size,
            background: "rgba(220,255,150,0.9)",
            boxShadow: "0 0 10px rgba(233,180,76,0.8)",
            opacity: o.opacity,
          }}
        />
      ) : (
        <img
          src={o.img}
          alt=""
          draggable="false"
          loading="lazy"
          style={{ width: o.size, height: "auto", opacity: o.opacity, filter: "drop-shadow(0 0 14px rgba(61,111,224,0.35))" }}
        />
      )}
    </div>
  </div>
);

// Global orbit belt — floats behind the content, above the wallpaper.
// Pulses gently in time with the background song while music is on.
export const OrbitField = () => {
  const reduce = useReducedMotion();
  const low = isLowPower();
  const [music, setMusic] = useState(isMusicOn());
  const [tempo, setTempo] = useState(getTempo());
  useEffect(() => onMusicChange(setMusic), []);
  useEffect(() => onTempoChange(setTempo), []);

  if (reduce) return null;
  // Owner asked for a calmer belt on the front page, brisker elsewhere.
  const items = (low ? ORBITERS.filter((o, i) => o.star || i < 1) : ORBITERS).map((o) => ({
    ...o,
    dur: o.dur * 1.15 * tempo,
  }));

  return (
    <motion.div
      data-testid="orbit-field"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[1] overflow-hidden"
      animate={music && !low ? { scale: [1, 1.008, 1] } : { scale: 1 }}
      transition={{ duration: 1.06 * tempo, repeat: Infinity, ease: "easeInOut" }}
    >
      {items.map((o, i) => (
        <Orbiter key={i} o={o} reduced={false} />
      ))}
    </motion.div>
  );
};
